import React, { useState, useEffect } from 'react';
import AppSearchSelect from './AppSearchSelect';
import ContactSearchSelect from './ContactSearchSelect';

// Stage options
const STAGE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'active', label: 'Active' },
  { value: 'converted', label: 'Converted' },
  { value: 'inactive', label: 'Inactive' },
];

export default function OpportunityForm({ 
  opportunity = null,
  preselectedContactId = null,
  preselectedContactName = null,
  onSubmit, 
  onCancel, 
  isSubmitting = false
}) {
  const [formData, setFormData] = useState({
    contactId: preselectedContactId || '',
    title: '',
    description: '',
    stage: 'new',
    estimatedInitialValue: '',
    estimatedMonthlyValue: '',
    actualInitialValue: '',
    actualMonthlyValue: '',
    linkedAppId: '',
    nextAction: '',
    nextActionDue: '',
    probability: '50',
    expectedCloseDate: '',
    notes: '',
  });

  useEffect(() => {
    if (opportunity) {
      setFormData({
        contactId: opportunity.contactId || preselectedContactId || '',
        title: opportunity.title || '',
        description: opportunity.description || '',
        stage: opportunity.stage || 'new',
        estimatedInitialValue: opportunity.estimatedInitialValue || '',
        estimatedMonthlyValue: opportunity.estimatedMonthlyValue || '',
        actualInitialValue: opportunity.actualInitialValue || '',
        actualMonthlyValue: opportunity.actualMonthlyValue || '',
        linkedAppId: opportunity.linkedAppId || '',
        nextAction: opportunity.nextAction || '',
        nextActionDue: opportunity.nextActionDue ? new Date(opportunity.nextActionDue).toISOString().slice(0, 10) : '',
        probability: opportunity.probability?.toString() || '50',
        expectedCloseDate: opportunity.expectedCloseDate || '',
        notes: opportunity.notes || '',
      });
    }
  }, [opportunity, preselectedContactId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isConverted = formData.stage === 'converted';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Contact Selection - only show if not preselected */}
      {!preselectedContactId && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact <span className="text-red-500">*</span>
          </label>
          <ContactSearchSelect
            value={formData.contactId}
            onChange={(contactId) => setFormData(prev => ({ ...prev, contactId: contactId || '' }))}
          />
        </div>
      )}
      
      {preselectedContactName && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
          <p className="text-gray-900">{preselectedContactName}</p>
        </div>
      )}

      {/* Title - Required */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          placeholder="e.g., Internal Tool, External App"
        />
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          placeholder="Brief description of the opportunity..."
        />
      </div>

      {/* Stage & Probability - Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="stage" className="block text-sm font-medium text-gray-700 mb-1">
            Stage
          </label>
          <select
            id="stage"
            name="stage"
            value={formData.stage}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          >
            {STAGE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="probability" className="block text-sm font-medium text-gray-700 mb-1">
            Win Probability (%)
          </label>
          <input
            type="number"
            id="probability"
            name="probability"
            value={formData.probability}
            onChange={handleChange}
            min="0"
            max="100"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          />
        </div>
      </div>

      {/* Estimated Values */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Estimated Values</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="estimatedInitialValue" className="block text-sm font-medium text-gray-700 mb-1">
              Initial (One-time) ($)
            </label>
            <input
              type="number"
              id="estimatedInitialValue"
              name="estimatedInitialValue"
              value={formData.estimatedInitialValue}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
              placeholder="5000"
            />
          </div>
          <div>
            <label htmlFor="estimatedMonthlyValue" className="block text-sm font-medium text-gray-700 mb-1">
              Monthly (Recurring) ($)
            </label>
            <input
              type="number"
              id="estimatedMonthlyValue"
              name="estimatedMonthlyValue"
              value={formData.estimatedMonthlyValue}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
              placeholder="500"
            />
          </div>
        </div>
      </div>

      {/* Actual Values - Only show for Converted deals */}
      {isConverted && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Actual Values (Confirmed)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="actualInitialValue" className="block text-sm font-medium text-gray-700 mb-1">
                Initial (One-time) ($)
              </label>
              <input
                type="number"
                id="actualInitialValue"
                name="actualInitialValue"
                value={formData.actualInitialValue}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
                placeholder="5000"
              />
            </div>
            <div>
              <label htmlFor="actualMonthlyValue" className="block text-sm font-medium text-gray-700 mb-1">
                Monthly (Recurring) ($)
              </label>
              <input
                type="number"
                id="actualMonthlyValue"
                name="actualMonthlyValue"
                value={formData.actualMonthlyValue}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
                placeholder="500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Next Action */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Next Action</h4>
        <div className="space-y-4">
          <div>
            <label htmlFor="nextAction" className="block text-sm font-medium text-gray-700 mb-1">
              Action Description
            </label>
            <input
              type="text"
              id="nextAction"
              name="nextAction"
              value={formData.nextAction}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
              placeholder="e.g., Send proposal, Schedule demo"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nextActionDue" className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                id="nextActionDue"
                name="nextActionDue"
                value={formData.nextActionDue}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
              />
            </div>
            <div>
              <label htmlFor="expectedCloseDate" className="block text-sm font-medium text-gray-700 mb-1">
                Expected Close Date
              </label>
              <input
                type="date"
                id="expectedCloseDate"
                name="expectedCloseDate"
                value={formData.expectedCloseDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Linked App */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Linked App
        </label>
        <AppSearchSelect
          value={formData.linkedAppId}
          onChange={(appId) => setFormData(prev => ({ ...prev, linkedAppId: appId || '' }))}
          selectedAppName={opportunity?.linkedAppName}
        />
        <p className="mt-1 text-xs text-gray-500">Link to an app if this opportunity results in one</p>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          placeholder="Additional notes about this opportunity..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting || (!preselectedContactId && !formData.contactId)}
          className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : opportunity ? 'Update Opportunity' : 'Create Opportunity'}
        </button>
      </div>
    </form>
  );
}
